import { useEffect, useState } from 'react'
import { Button, Card, Input, Modal, Select, Space, Table, Tabs, Typography, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { adminApi, formatApiError } from '../../api'
import { PageHeader, formatDateOrDash } from '../../components/ui'

type ApiKeyRow = {
  id: number
  name: string
  key_prefix: string
  is_active: boolean
  rate_limit_per_min: number
  dataset_ids: number[]
  created_at: string
}

type DatasetOpt = { id: number; name: string }

type LogRow = {
  id: number
  endpoint: string
  query_text: string
  hit_count: number
  client_ip: string
  created_at: string
}

export default function OpenApiManage() {
  const [keys, setKeys] = useState<ApiKeyRow[]>([])
  const [logs, setLogs] = useState<LogRow[]>([])
  const [datasets, setDatasets] = useState<DatasetOpt[]>([])
  const [docs, setDocs] = useState<Record<string, unknown> | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [keyName, setKeyName] = useState('')
  const [keyDatasets, setKeyDatasets] = useState<number[]>([])
  const [rawKey, setRawKey] = useState('')
  const [creating, setCreating] = useState(false)

  const loadKeys = () => {
    adminApi
      .get<{ items: ApiKeyRow[] }>('/admin/open-api/keys')
      .then((r) => setKeys(r.data.items || []))
      .catch(() => {})
  }

  const loadLogs = () => {
    adminApi
      .get<{ items: LogRow[] }>('/admin/open-api/logs', { params: { page: 1, page_size: 20 } })
      .then((r) => setLogs(r.data.items || []))
      .catch(() => {})
  }

  useEffect(() => {
    loadKeys()
    loadLogs()
    adminApi
      .get<{ items: DatasetOpt[] }>('/admin/datasets/options', { params: { published_only: true } })
      .then((r) => setDatasets(r.data.items || []))
      .catch(() => {})
    adminApi
      .get('/admin/open-api/docs')
      .then((r) => setDocs(r.data as Record<string, unknown>))
      .catch(() => {})
  }, [])

  const createKey = () => {
    if (!keyName.trim() || !keyDatasets.length) {
      message.warning('请填写名称并选择数据集')
      return
    }
    setCreating(true)
    adminApi
      .post<{ raw_key: string }>('/admin/open-api/keys', {
        name: keyName.trim(),
        dataset_ids: keyDatasets,
      })
      .then((r) => {
        setRawKey(r.data.raw_key)
        setKeyName('')
        setKeyDatasets([])
        loadKeys()
      })
      .catch((e) => message.error(formatApiError(e, '创建失败')))
      .finally(() => setCreating(false))
  }

  const deleteKey = (id: number) => {
    Modal.confirm({
      title: '删除此 API Key？',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () =>
        adminApi.delete(`/admin/open-api/keys/${id}`).then(() => {
          message.success('已删除')
          loadKeys()
        }),
    })
  }

  return (
    <div>
      <PageHeader
        title="开放 API"
        description="为第三方 Agent / 知识库平台签发密钥；接口基础路径 /api/open/v1"
      />
      <Card>
        <Tabs
          items={[
            {
              key: 'keys',
              label: 'API 密钥',
              children: (
                <>
                  <Space style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
                      新建密钥
                    </Button>
                    <Link to="/manage/dataset-sync">同步任务</Link>
                  </Space>
                  <Table<ApiKeyRow>
                    rowKey="id"
                    dataSource={keys}
                    pagination={false}
                    columns={[
                      { title: '名称', dataIndex: 'name' },
                      { title: '前缀', dataIndex: 'key_prefix', width: 120 },
                      { title: '限流/分', dataIndex: 'rate_limit_per_min', width: 88 },
                      {
                        title: '数据集',
                        dataIndex: 'dataset_ids',
                        render: (ids: number[]) => ids.join(', '),
                      },
                      {
                        title: '操作',
                        width: 80,
                        render: (_, r) => (
                          <Button size="small" danger onClick={() => deleteKey(r.id)}>
                            删除
                          </Button>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'docs',
              label: '接口文档',
              children: (
                <pre style={{ background: '#fafafa', padding: 12, borderRadius: 8, overflow: 'auto' }}>
                  {JSON.stringify(docs, null, 2)}
                </pre>
              ),
            },
            {
              key: 'logs',
              label: '调用审计',
              children: (
                <Table<LogRow>
                  rowKey="id"
                  dataSource={logs}
                  pagination={false}
                  columns={[
                    { title: '时间', dataIndex: 'created_at', width: 180, render: (v: string) => formatDateOrDash(v) },
                    { title: '接口', dataIndex: 'endpoint', width: 120 },
                    { title: '问句', dataIndex: 'query_text', ellipsis: true },
                    { title: '命中', dataIndex: 'hit_count', width: 64 },
                    { title: 'IP', dataIndex: 'client_ip', width: 120 },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title="新建 API Key"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false)
          setRawKey('')
        }}
        onOk={rawKey ? () => setModalOpen(false) : createKey}
        confirmLoading={creating}
        okText={rawKey ? '关闭' : '创建'}
        cancelText="取消"
        cancelButtonProps={rawKey ? { style: { display: 'none' } } : undefined}
      >
        {rawKey ? (
          <>
            <Typography.Paragraph type="warning">请立即复制，关闭后无法再次查看：</Typography.Paragraph>
            <Input.TextArea value={rawKey} readOnly autoSize />
          </>
        ) : (
          <>
            <Input
              placeholder="密钥名称"
              value={keyName}
              style={{ marginBottom: 12 }}
              onChange={(e) => setKeyName(e.target.value)}
            />
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="选择可访问的数据集"
              value={keyDatasets}
              onChange={setKeyDatasets}
              options={datasets.map((d) => ({ value: d.id, label: d.name }))}
            />
          </>
        )}
      </Modal>
    </div>
  )
}
